import { matchPath, useLocation } from 'react-router-dom'
import type { AppRedirectProps } from 'lib/routing/AppRedirect'
import { AppRedirect } from 'lib/routing/AppRedirect'
import { stringifyTo, useSsrRoutingContext } from 'lib/routing/ssrRoutingContext'

interface Props extends AppRedirectProps {
  code?: number
  exact?: boolean
  from?: string
  to: AppRedirectProps['to']
  withSearchAndHash?: boolean
}

function getRedirectTo(
  to: AppRedirectProps['to'],
  location: ReturnType<typeof useLocation>,
  withSearchAndHash: boolean,
): AppRedirectProps['to'] {
  // When false, the caller has already included search and hash in `to`; use it as-is.
  if (!withSearchAndHash) {
    return to
  }

  if (typeof to === 'string') {
    const parsedTo = parseRedirectToString(to)

    return {
      pathname: parsedTo.pathname,
      search: parsedTo.search || location.search,
      hash: parsedTo.hash || location.hash,
    }
  }

  return {
    ...to,
    search: to.search ?? location.search,
    hash: to.hash ?? location.hash,
  }
}

function parseRedirectToString(to: string) {
  const [pathAndSearch, hash = ''] = to.split('#')
  const [pathname, search = ''] = pathAndSearch.split('?')

  return {
    hash: hash ? `#${hash}` : '',
    pathname,
    search: search ? `?${search}` : '',
  }
}

function interpolateRedirectTo(
  to: AppRedirectProps['to'],
  params: Record<string, string | undefined>,
): AppRedirectProps['to'] {
  if (typeof to === 'string') {
    return interpolateRedirectPath(to, params)
  }

  return {
    ...to,
    hash: to.hash ? interpolateRedirectPath(to.hash, params) : to.hash,
    pathname: to.pathname ? interpolateRedirectPath(to.pathname, params) : to.pathname,
    search: to.search ? interpolateRedirectPath(to.search, params) : to.search,
  }
}

function interpolateRedirectPath(
  value: string,
  params: Record<string, string | undefined>,
): string {
  const interpolated = value
    .replace(/:([A-Za-z0-9_]+)\?/g, (_token, key: string) => {
      const param = params[key]
      return param ? encodeURIComponent(param) : ''
    })
    .replace(/:([A-Za-z0-9_]+)/g, (_token, key: string) => {
      const param = params[key]
      return param ? encodeURIComponent(param) : ''
    })

  if (!value.startsWith('/')) {
    return interpolated
  }

  return interpolated
    .replace(/\/{2,}/g, '/')
    .replace(/\/(?=[?#]|$)/, '') || '/'
}

function RedirectWithStatus(props: Props) {
  const { code, exact, from, withSearchAndHash = true, ...redirectProps } = props
  const location = useLocation()
  const ssrRoutingContext = useSsrRoutingContext()
  const fromMatch = from ? matchPath({ path: from, end: !!exact }, location.pathname) : null

  if (from && !fromMatch) {
    return null
  }

  const redirectTo = fromMatch ? interpolateRedirectTo(redirectProps.to, fromMatch.params) : redirectProps.to
  const to = getRedirectTo(redirectTo, location, withSearchAndHash)

  if (ssrRoutingContext) {
    ssrRoutingContext.statusCode = code || 302
    ssrRoutingContext.url = stringifyTo(to)
  }

  return <AppRedirect
    {...redirectProps}
    to={to}
  />
}

export default RedirectWithStatus
